<template>
  <v-app id="app" v-if="!isLoading">
    <v-container fluid class="d-print-none">
      <v-app-bar
        fixed
        class="d-print-none"
        :color="isProductionBuild ? colourBasedOnProduction : colourBasedOnApiTarget"
        v-if="(user && !user.none) && !hideTopNav"
        hide-on-scroll
      >
        <v-btn
          v-if="useNewMenu"
          rounded
          text
          @click="showNavMenu = !showNavMenu"
          data-cy="nav-open"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>
        <v-menu offset-y v-else-if="user">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              rounded
              text
              data-cy="old-menu-open"
            >
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </template>
          <v-list style="width:auto">
            <v-list-item-group>
              <v-list-item style="min-height: 0">
                <v-row no-gutters>
                  <v-col v-for="(linkChunk,index) of linkChunks" v-bind:key="index">
                    <v-list dense>
                      <v-list-item :style="links.length>10?'min-height: 0':''"
                                   v-for="link of linkChunk" v-bind:key="link.name"
                      >
                        <router-link :to="link">{{ toWords(link.name) }}</router-link>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <v-divider/>
          <account-list
            class="px-4 py-3"
            :user="user"
            :fed-browser-id="fedBrowserId"
            :api-target="apiTarget"
            :use-new-menu="useNewMenu"
            @choose-api="chooseApi"
            @logout="confirmLogout"
            @use-new-menu="chooseMenu"
          />
        </v-menu>
        <v-spacer/>
        <v-breadcrumbs>
          <v-breadcrumbs-item v-for="page of history" v-bind:key="page.name">
            <v-btn outlined text :to="page">{{ toWords(page.name) }}</v-btn>
          </v-breadcrumbs-item>
        </v-breadcrumbs>
        <v-spacer/>
        <div class="mt-4" v-if="hasDates">
          <v-dialog v-model="showNextProduction" max-width="600">
            <v-card>
              <v-card-title>Next Production</v-card-title>
              <v-card-text>
                You are looking at the next production of meals for the following days
                <ul>
                  <li v-for="d of formattedNextProductionDays" v-bind:key="d">
                    {{ d }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-dialog>
          <v-dialog v-model="showNotNextProduction" max-width="600">
            <v-card>
              <v-card-title>Other Production Days</v-card-title>
              <v-card-text>
                This is a warning to let you know you may not be looking at the right production dates.
                Based on it being {{ today }}, the next meal production dates are:
                <ul>
                  <li v-for="d of formattedNextProductionDays" v-bind:key="d">
                    {{ d }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-dialog>
          <v-alert @click="showNextProduction=true" dense v-if="isNextProduction" color="success">Next Production
          </v-alert>
          <v-alert @click="showNotNextProduction=true" dense v-if="!isNextProduction" color="warning">Other Production
            Dates
          </v-alert>
        </div>
        <router-view name="toolbar"/>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :class="isStale ? 'refresh-stale':''"
              v-bind="attrs"
              v-on="on"
              icon
              :loading="isReloading"
              @click="doReload()"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <div>click to refresh data from server to ensure you are using the most recent amounts</div>
          <div v-if="isStale">data has not been updated in {{ minutesUntilStale }} minutes</div>

        </v-tooltip>
        <!-- the print macro renderer on the api prints the page; only roles with the macros page get the button -->
        <v-tooltip bottom v-if="canRenderPdf">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              :loading="pdfSaving"
              :disabled="pdfSaving"
              @click="savePagePdf"
              data-cy="page-pdf"
            >
              <v-icon>mdi-file-pdf-box</v-icon>
            </v-btn>
          </template>
          <div>save this page as a pdf, printed on the server as it prints here</div>
        </v-tooltip>
      </v-app-bar>

      <v-spacer class="d-print-none pb-14" v-if="!hideTopNav"/>

      <nav-menu
        v-if="useNewMenu && user && !user.none"
        v-model="showNavMenu"
        :location="navLocation"
        :pages="pages"
        @use-old-menu="chooseMenu(false)"
      >
        <template v-slot:account="{parts}">
          <account-list
            :user="user"
            :fed-browser-id="fedBrowserId"
            :api-target="apiTarget"
            :use-new-menu="useNewMenu"
            :parts="parts"
            @choose-api="chooseApi"
            @logout="confirmLogout"
            @use-new-menu="chooseMenu"
          />
        </template>
      </nav-menu>


    </v-container>
    <!-- keep alive means components don't re-render
         (so a search on a datatable will stay at the same place when you navigate back
         -->

    <keep-alive include="MealDesign,MealDesignAdmin,MealScheduleTemplate,CustomersLookup">
      <router-view :key="$route.name"/>
    </keep-alive>

    <v-snackbar
      v-model="showErrors"
      color="warning"
      top
      dark
      :timeout="errorMessageTimeout"
    >
      Something unexpected happened. Please refresh the page and check your work.
      <br/>
      <ul>
        <li v-for="error of errors" v-bind:key="error">
          {{ error }}
        </li>
      </ul>
      <!--      <template v-slot:action="{ attrs }">-->
      <!--        <v-btn-->
      <!--            icon-->
      <!--            v-bind="attrs"-->
      <!--            @click="showErrors = false"-->
      <!--        >-->
      <!--          <v-icon>mdi-close</v-icon>-->
      <!--        </v-btn>-->
      <!--      </template>-->
    </v-snackbar>

    <snackbar-notification/>
    <macro-recorder-bar/>
  </v-app>
</template>

<style lang="scss">

@import "assets/app.scss";

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  //text-align: center;
  color: #2c3e50;
}

#nav {
  //padding: 30px;
  //height: 30px;

  //a {
  //  font-weight: bold;
  //  color: #2c3e50;
  //
  //  &.router-link-exact-active {
  //    color: #42b983;
  //  }
  //}
}

.refresh-stale {
  background: tomato;
}
</style>
<script>
import {mapActions, mapGetters} from 'vuex';
import api from './api';
import moment from 'moment';
import * as Sentry from "@sentry/vue";
import {extractProductionFromDateProduction, getNextProductionDays, getProductionDays} from "@/store/utils";
import SnackbarNotification from '@/components/SnackbarNotification.vue';
import MacroRecorderBar from '@/components/MacroRecorderBar.vue';
import NavMenu from '@/components/NavMenu.vue';
import AccountList from '@/components/AccountList.vue';
import {navSections} from '@/nav';
import {flattenNav} from '@/nav-helpers';
import {loadUseNewMenu, saveUseNewMenu} from '@/nav-settings';
import {pageRenderRequest} from '@/utils/macro-steps';
import {fetchRenderedPdf, followRender, startRender} from '@/utils/macro-render-client';
import {downloadBlob} from '@/utils/download';

const {mapState} = require('vuex');
let errorTimeout;

export default {
  components: {
    SnackbarNotification,
    MacroRecorderBar,
    NavMenu,
    AccountList
  },
  async created() {
    setTimeout(() => this.isStale = true, this.minutesUntilStale * 60 * 1000);
    // check this on a schedule in case someone leaves the window open
    setInterval(() => this.checkProductionState(), this.minutesUntilStale * 60 * 1000);
    // for (let i = 0; i < 7; i++) {
    //   const d = moment('2024-07-07').add(i, 'days');
    //   const nextProductionDates = getNextProductionDays(d).map(d => `${moment(d).format('ddd')} (${d})`);
    //   console.log(`looking at k.fed on ${d.format('ddd')} (${d.format(moment.HTML5_FMT.DATE)}) you should see ${nextProductionDates.join(', ')}`);
    // }
    this.checkProductionState();
    const date = (this.$route.params.date && moment(this.$route.params.date)) || false;
    if (date) {
      this.setDate(date.format(moment.HTML5_FMT.DATE));
    }
    const user = await this.fetchCurrentUser();
    try {
      Sentry.setUser(user);
    } catch (e2) {
      console.warn('sentry setUser failed', e2);
    }

    this.isLoading = false;
    const self = this;
    api.setErrorHandler((e) => {

      console.log('error handler called: ', e);
      let message = (e && e.response && e.response.data && e.response.data.error) || e.message;
      if (typeof message !== 'string') {
        message = JSON.stringify(message);
      }
      if (e.config) {
        const {url, method} = e.config;
        message = `${method} ${url} - ${message}`;
      }
      if (!self.errors.includes(message)) {
        self.errors.push(message);
      }
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
      errorTimeout = setTimeout(() => {
        errorTimeout = undefined;
        self.errors = [];
      }, self.errorMessageTimeout);

      try {
        Sentry.captureException(e);
      } catch (e2) {
        console.warn('sentry failed to capture', e);
        console.warn('sentry error', e2);
      }
    });
  },
  mounted() {
    this.$router.onReady(() => {
        const storageKey = 'k.fed-browser-id';
        const browserId = window.localStorage.getItem(storageKey);
        if (browserId) {
          this.fedBrowserId = browserId.split('-').slice(0, 2).join('-');
        }
        const dates = this.$route.query.dates;
        if (dates && Array.isArray(dates)) {
          const [date] = this.$route.query.dates.sort();
          if (moment().subtract(2, 'month').isAfter(date)) {
            if (confirm(`It looks like you are opening a link with a date from over 2 months ago (${date}).  Are you sure you want to load this old date?\n\nYou may want to remove this link from your browser history.`)) {
              console.log('ok with old date', date);
            } else {
              console.log('redirecting because old date in link');
              const {name} = this.$route;
              this.$router.replace({name})
            }
          }
        }
      }
    );
  },
  watch: {
    dates: 'checkProductionState',
    date: 'checkProductionState',
    errors() {
      this.showErrors = this.errors.length > 0;
    },
    $route(val) {
      // console.log('adding', val);
      const index = this.history.findIndex(page => page.name === val.name);
      if (index < 0) {
        this.history.push(val);
      } else {
        this.history[index] = val;
      }
      if (this.history > 5) {
        this.history.shift();
      }
    }
  },
  data() {
    return {
      isLoading: true,
      isReloading: false,
      pdfSaving: false,
      kitchen_v1_url: process.env.V1_URL || 'https://fed-kitchen.netlify.app/',
      apiTarget: api.apiTarget,
      isProductionBuild: api.isProductionBuild,
      isStale: false,
      history: [],
      errors: [],
      showErrors: false,
      errorMessageTimeout: 5000,
      minutesUntilStale: 15,
      isNextProduction: null,
      showNextProduction: null,
      showNotNextProduction: null,
      fedBrowserId: null,
      showNavMenu: false,
      useNewMenu: loadUseNewMenu(),
    }
  },
  computed: {
    ...mapState([
      'user',
      'hideTopNav'
    ]),
    ...mapGetters([
      'date'
    ]),
    production() {
      return this.$route.query.production || extractProductionFromDateProduction(this.$route.params.date);
    },
    api() {
      return api;
    },
    formattedNextProductionDays() {
      return getNextProductionDays()
        .map(d => {
          d = moment(d);
          return `${d.format('dddd')} (${d.format('LL')})`;
        });
    },
    today() {
      const d = moment();
      return `${d.format('dddd')} (${d.format('LL')})`
    },
    dateWithProduction() {
      const dates = ((Array.isArray(this.dates) ? this.dates : [this.dates]) || []).filter(d => !!d).sort();
      const date = this.date || dates[0];
      if (this.production) {
        return `${date}_${this.production}`;
      } else {
        return date;
      }
    },
    dates() {
      return this.$route.query.dates;
    },
    /** the old menu: the pages the role can open, as one list */
    links() {
      return flattenNav(navSections)
        .map(({item}) => item)
        .filter(item => !item.needs && this.pages.includes(item.name))
        .map(item => this.navLocation(item))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    isAdmin() {
      const user = this.user;
      return !!user && (user.roleNames
        ? user.roleNames.includes('admin')
        : user.roleName === 'admin');
    },
    pages() {
      const user = this.user;
      if (user.roleDetail) {
        //console.log('calc pages', this.user);
        const {readPages, writePages} = user.roleDetail;
        const pages = new Set([...readPages, ...writePages]);
        if (this.isAdmin) {
          return this.$router.getRoutes().map(r => r.name);
        } else if (pages.size > 0) {
          return [...pages];
        }
      }

      // otherwise no pages
      return [];
    },
    /** the pdf button uses the print macro renderer, so it goes with the Print Macros page (admins have every page) */
    canRenderPdf() {
      return this.pages.includes('PrintMacros');
    },
    pageTitle() {
      return this.$route.meta.title;
    },
    startOfWeek() {
      return this.dateText(moment(this.date).startOf('week'));
    },
    linkChunks() {
      const links = [...this.links];
      const chunks = [];
      const chunkSize = 20;
      while (links.length > 0) {
        chunks.push(links.splice(0, chunkSize));
      }
      return chunks;
    },
    hasDates() {
      return (this.dates && this.dates.length > 0);
    },
    colourBasedOnProduction() {
      const colorMap = {
        local: '',
        ship: 'blue',
        other: 'orange'
      }
      return colorMap[this.production];
    },
    /** a development build shows which api it talks to: local is the quiet one, live shouts */
    colourBasedOnApiTarget() {
      const colorMap = {
        local: 'yellow',
        staging: 'cyan',
        production: 'pink',
      };
      return colorMap[this.apiTarget] || 'yellow';
    },
  },
  methods: {
    getProductionDays,
    /**
     * The pdf button in the app bar: this page as a one page print macro, rendered by the api
     * server's chrome (as it prints: print media, css page sizes) and downloaded here under the
     * name a macro would give it.
     */
    async savePagePdf() {
      this.pdfSaving = true;
      const {name, path, query} = this.$route;
      const request = pageRenderRequest({name, path, query}, location => this.$router.resolve(location).href.replace(/^#/, ''));
      const [{filename}] = request.steps;
      try {
        const job = await startRender(request);
        this.$store.commit('showSnackbar', {message: `printing ${filename} on the server...`, color: 'info', timeout: -1});
        const finished = await followRender(job.id).finished;
        const [step] = finished.steps;
        if (!step.status.startsWith('done')) {
          throw new Error(step.error || finished.error || `the render ${finished.status}`);
        }
        downloadBlob(await fetchRenderedPdf(finished.id, step.index), filename, 'application/pdf');
        const note = step.status === 'done-unsettled' ? ` (the page was still loading: ${(step.reasons || []).join(', ')})` : '';
        this.$store.commit('showSnackbar', {message: `saved ${filename}${note}`, color: note ? 'warning' : 'success', timeout: 5000});
      } catch (e) {
        console.error('pdf failed', e);
        const reason = (e.response && e.response.data && e.response.data.error) || e.message || e;
        this.$store.commit('showSnackbar', {message: `pdf failed: ${reason}`, color: 'error', timeout: 8000});
      } finally {
        this.pdfSaving = false;
      }
    },
    ...mapActions([
      'reload',
      'fetchCurrentUser',
      'logout',
      'setDate'
    ]),
    /** a nav item as a router location, carrying the dates being looked at where the page uses them */
    navLocation(item) {
      const {name} = item;
      const dates = ((Array.isArray(this.dates) ? this.dates : [this.dates]) || []).filter(d => !!d).sort();
      const production = this.production || '';
      switch (item.dates) {
        case 'production':
          return {name, query: {dates, production}};
        case 'week':
          return {name, query: {dates: [this.startOfWeek]}};
        case 'meals':
          return {name, params: {date: this.dateWithProduction}};
        default:
          return {name};
      }
    },
    dateText(d) {
      return moment(d).format('YYYY-MM-DD');
    },
    async doReload() {
      this.isReloading = true;
      const minimumTimePromise = new Promise((resolve) => {
        // may seem weird, but if it is too fast, people may want to mash the button
        // so we make sure it looks like it is loading for at least 1 sec
        setTimeout(() => resolve(), 1000);
      });
      // await this
      //   .reload()
      //   .catch(e => {
      //     console.warn('reload had some errors', e);
      //   });
      window.location.reload();
      await minimumTimePromise;
      this.isReloading = false;
    },
    /** switch between the old menu and the new one, remembered in this browser */
    chooseMenu(useNewMenu) {
      this.useNewMenu = useNewMenu;
      this.showNavMenu = false;
      saveUseNewMenu(useNewMenu);
    },
    confirmLogout() {
      if (confirm('Do you want to log out?')) {
        this.logout();
      }
    },
    chooseApi(target) {
      const previous = this.apiTarget;
      if (!target || target === previous) {
        return;
      }
      this.apiTarget = target;
      this.$nextTick(() => {
        if (confirm(`would you like to switch to the ${api.apiTargets[target].label} api?`)) {
          api.useApiTarget(target);
        } else {
          this.apiTarget = previous;
        }
      });
    },

    /**
     * converts 'StudlyCaps' into 'Studly Caps'
     * @param s
     * @returns {*}
     */
    toWords(s) {
      return s.replace(/([A-Z])/g, (match, p1) => ` ${p1}`).trim();
    },
    checkProductionState() {
      const nextProductionDays = getNextProductionDays(moment());
      // this is for checking order edit
      if (this.dates && this.dates.length === 2) {
        let [dateStart, dateEnd] = this.dates.slice().sort();

        // if the date range is a week - do this other logic - used on order-edit and meal audit pages
        if (moment(dateStart).day() === 0 && moment(dateEnd).diff(dateStart, 'days') === 6) {
          const dayStart = moment(dateStart);
          if (dayStart.day() === 0) {
            dateStart = dayStart.add(1, 'days').format(moment.HTML5_FMT.DATE);
          }
          dateStart = moment(dateStart).format(moment.HTML5_FMT.DATE)
          console.log('checking ', dateStart, dateEnd);
          this.isNextProduction = nextProductionDays.includes(dateStart) || nextProductionDays.includes(dateEnd);
          // break out
          return;
        }
      }

      // fallback/default
      const date = (this.dates && this.dates[0]) || this.date;
      this.isNextProduction = nextProductionDays.includes(date);
    },

  }
}
</script>
